import { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  CloudUpload, Database, RotateCcw, Download, Trash2, ShieldAlert,
  RefreshCcw, CalendarClock, HardDriveDownload, CheckCircle2,
} from "lucide-react";

function fmtBytes(n) {
  const x = Number(n || 0);
  if (x < 1024) return `${x} B`;
  if (x < 1024 * 1024) return `${(x / 1024).toFixed(1)} KB`;
  if (x < 1024 * 1024 * 1024) return `${(x / 1024 / 1024).toFixed(2)} MB`;
  return `${(x / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return s; }
}

export default function BackupRestore({ embedded = false }) {
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [confirmFile, setConfirmFile] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/backups"),
        api.get("/admin/backups/last"),
      ]);
      setItems(a.data?.items || []);
      setStatus({ ...a.data, last_backup: b.data?.last_backup || null });
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const runBackupNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/backups/run");
      toast.success(`Backup complete — ${r.data.total_documents} docs across ${r.data.collections_count} collections`);
      await refresh();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setRunning(false);
    }
  };

  const askRestore = (f) => { setConfirmFile(f); setConfirmText(""); };

  const confirmRestore = async () => {
    if (!confirmFile) return;
    setRestoring(true);
    try {
      const r = await api.post("/admin/backups/restore", {
        file_id: confirmFile.id,
        confirm: "RESTORE",
      });
      toast.success(`Restore complete — ${r.data.total_documents_loaded} documents reloaded`);
      setConfirmFile(null);
      setConfirmText("");
      await refresh();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setRestoring(false);
    }
  };

  const deleteBackup = async (f) => {
    if (!confirm(`Delete backup "${f.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/backups/${f.id}`);
      toast.success("Backup deleted");
      await refresh();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const [cleaning, setCleaning] = useState(false);
  const cleanTestData = async () => {
    if (!window.confirm("Delete all test/demo data and the old starter accounts (HR Admin, Sales Admin, Trainer One, etc.) with everything linked to them? Your Master Admin login stays. This cannot be undone — take a backup first.")) return;
    setCleaning(true);
    try {
      const r = await api.post("/admin/cleanup-demo-data");
      toast.success(`Cleanup done — ${r.data.total} records removed (${r.data.deleted?.users || 0} accounts)`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setCleaning(false);
    }
  };

  const last = status?.last_backup;

  return (
    <div className="space-y-6" data-testid="backup-restore-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        {!embedded ? (
          <div>
            <h1 className="heading text-2xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
              <Database size={22} className="text-indigo-600" />
              Backup &amp; Restore
            </h1>
            <p className="text-[12.5px] text-slate-500 mt-1">
              Automatic daily snapshot of the entire database, stored securely inside your app.
              Restore overwrites all current data with the chosen archive.
            </p>
          </div>
        ) : (
          <div className="text-[11px] text-slate-500 dark:text-slate-400 max-w-2xl">
            Automatic daily snapshot of the entire database, stored securely inside your app. Restore overwrites all current data with the chosen archive.
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={refresh}
            disabled={loading}
            data-testid="backup-refresh"
          >
            <RefreshCcw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={cleanTestData}
            disabled={cleaning}
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
            data-testid="cleanup-test-data-btn"
          >
            <Trash2 size={14} className="mr-1.5" />
            {cleaning ? "Cleaning…" : "Clean Test Data"}
          </Button>
          <Button
            onClick={runBackupNow}
            disabled={running || !status?.configured}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="backup-run-now"
          >
            <CloudUpload size={14} className="mr-1.5" />
            {running ? "Backing up…" : "Backup Now"}
          </Button>
        </div>
      </div>

      {/* Status panel */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <CalendarClock size={12} /> Schedule
          </div>
          <div className="mt-1.5 text-sm font-semibold text-slate-900">
            Daily at {String(status?.schedule_hour_ist ?? 22).padStart(2, "0")}:00 IST
          </div>
          <div className="text-[11.5px] text-slate-500 mt-0.5">
            Keep last <b>{status?.retention ?? 14}</b> archives (older ones auto-pruned)
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <CheckCircle2 size={12} /> Last Backup
          </div>
          <div className="mt-1.5 text-sm font-semibold text-slate-900">
            {last ? fmtDate(last.created_at) : "—"}
          </div>
          <div className="text-[11.5px] text-slate-500 mt-0.5">
            {last
              ? `${last.stats?.total_documents ?? 0} docs · ${fmtBytes(last.size)}`
              : "Run a manual backup to create the first archive"}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <HardDriveDownload size={12} /> Storage
          </div>
          <div className="mt-1.5 text-sm font-semibold text-slate-900 truncate">
            MongoDB Vault
          </div>
          <div className="text-[11.5px] text-slate-500 mt-0.5">
            Archives stored inside your database. Use <b>Download</b> to keep an off-site copy.
          </div>
        </div>
      </div>

      {/* List */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="text-[12.5px] font-bold uppercase tracking-wider text-slate-700">
            Stored Backups
          </div>
          <span className="text-[11px] text-slate-500">{items.length} archive(s)</span>
        </div>
        <div className="divide-y divide-slate-100">
          {loading ? (
            <div className="px-4 py-8 text-center text-[12.5px] text-slate-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="px-4 py-10 text-center" data-testid="backup-empty">
              <Database size={28} className="text-slate-300 mx-auto mb-2" />
              <div className="text-[13px] text-slate-700 font-semibold">No backups yet</div>
              <div className="text-[11.5px] text-slate-500 mt-1">
                Click <b>Backup Now</b> to create the first archive.
              </div>
            </div>
          ) : (
            items.map((f) => (
              <div key={f.id} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50/60">
                <div className="min-w-0">
                  <div className="text-[12.5px] font-semibold text-slate-900 truncate" data-testid={`backup-name-${f.id}`}>
                    {f.name}
                  </div>
                  <div className="text-[10.5px] text-slate-500 flex items-center gap-2">
                    <span>{fmtDate(f.createdTime)}</span>
                    <span>·</span>
                    <span>{fmtBytes(f.size)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/backups/${f.id}/download`}
                    className="inline-flex items-center text-[11px] text-slate-500 hover:text-indigo-600 px-2 py-1 rounded-md hover:bg-slate-100"
                    title="Download .zip"
                    data-testid={`backup-download-${f.id}`}
                  >
                    <Download size={13} />
                  </a>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => askRestore(f)}
                    className="text-[11px] h-7"
                    data-testid={`backup-restore-${f.id}`}
                  >
                    <RotateCcw size={12} className="mr-1" />
                    Restore
                  </Button>
                  <button
                    onClick={() => deleteBackup(f)}
                    className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-md p-1.5"
                    title="Delete"
                    data-testid={`backup-delete-${f.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Restore confirmation */}
      <Dialog open={!!confirmFile} onOpenChange={(o) => { if (!o) { setConfirmFile(null); setConfirmText(""); } }}>
        <DialogContent className="max-w-md" data-testid="backup-restore-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <ShieldAlert size={18} />
              Confirm Restore
            </DialogTitle>
            <DialogDescription className="text-[12.5px]">
              This will <b>WIPE the entire database</b> and replace it with the chosen archive.
              Existing data not present in the backup will be permanently lost.
            </DialogDescription>
          </DialogHeader>
          <div className="text-[12px] bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            <div className="font-semibold text-rose-900 break-all">{confirmFile?.name}</div>
            <div className="text-rose-700 mt-0.5">
              {fmtDate(confirmFile?.createdTime)} · {fmtBytes(confirmFile?.size)}
            </div>
          </div>
          <div className="mt-1">
            <Label className="text-[11px] uppercase font-bold text-slate-600">
              Type <b className="mono">RESTORE</b> to confirm
            </Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="RESTORE"
              className="mt-1"
              data-testid="backup-restore-confirm-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setConfirmFile(null); setConfirmText(""); }}>
              Cancel
            </Button>
            <Button
              onClick={confirmRestore}
              disabled={confirmText !== "RESTORE" || restoring}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="backup-restore-confirm-submit"
            >
              <RotateCcw size={14} className="mr-1.5" />
              {restoring ? "Restoring…" : "Wipe & Restore"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
